import { Component } from '@verndale/core';
import { gsap } from 'gsap';
import { isDesktop, isTabletLandscape, prefersReducedMotion } from '../helpers';
import Accordion from './accordion';

class Header extends Component {
  mainAccordion: Accordion | undefined;
  clickMegaMenuTimeline: gsap.core.Timeline | null = null;

  constructor(el: HTMLElement) {
    super(el);

    if (!isDesktop()) {
      this.mainAccordion = new Accordion(this.dom.resourcesAccordion as HTMLElement, 'grid');
    }
  }

  setupDefaults() {
    this.dom = {
      el: this.el,
      resourcesTriggerBtn: this.el.querySelector<HTMLElement>('.header__resources-btn'),
      mobileResourcesTriggerBtn: this.el.querySelector<HTMLElement>(
        '.mega-menu__resources-btn-container'
      ),
      resourcesMenu: this.el.querySelector<HTMLElement>('.resources-menu'),
      resourcesCloseBtn: this.el.querySelector<HTMLElement>('.resources-menu__close'),
      resourcesMenuItems: this.el.querySelectorAll<HTMLElement>('.btn-resources-menu-trigger'),
      resourcesMenuPanels: this.el.querySelectorAll<HTMLElement>('.resources-menu__item-panel'),
      resourcesAccordion: this.el.querySelector<HTMLElement>('.resources-menu__item-container'),
      megaMenu: this.el.querySelector<HTMLElement>('.mega-menu'),
      megaMenuTriggerBtn: this.el.querySelector<HTMLElement>('.header__mega-menu-btn'),
      megaMenuCloseBtn: this.el.querySelector<HTMLElement>('.mega-menu__close'),
      megamenuItems: this.el.querySelectorAll<HTMLElement>('.btn-mega-menu-trigger'),
      megamenuPanels: this.el.querySelectorAll<HTMLElement>('.mega-menu__item-panel'),
      megamenuBackTo: this.el.querySelectorAll<HTMLElement>('.mega-menu__back-to-btn'),
      searchTriggerBtn: this.el.querySelector<HTMLElement>('.header__search-btn'),
      searchMenu: this.el.querySelector<HTMLElement>('.search-menu'),
      searchMenuBtn: this.el.querySelector<HTMLElement>('.search-menu__search-btn'),
      searchMenuCloseBtn: this.el.querySelector<HTMLElement>('.search-menu__close'),
      searchInput: this.el.querySelector<HTMLElement>('.search-menu__search-input'),
      megaMenuSearchInput: this.el.querySelector<HTMLElement>('.mega-menu__search-input'),
      megaMenuSearchBtn: this.el.querySelector<HTMLElement>('.mega-menu__search-btn'),
      megaMenuSearchForm: this.el.querySelector<HTMLElement>('.mega-menu__search-container form'),
      searchMenuForm: this.el.querySelector<HTMLElement>('.search-menu__search-container form'),
      megamenuContent: this.el.querySelector<HTMLElement>('.mega-menu__content'),
      resourcesMenuContent: this.el.querySelector<HTMLElement>('.resources-menu__content'),
      megamenuDecorator: this.el.querySelector<HTMLElement>('.mega-menu__item-indicator-decorator'),
      resourcesMenuDecorator: this.el.querySelector<HTMLElement>(
        '.resources-menu__item-indicator-decorator'
      ),
      resourcesRightPanel: this.el.querySelector<HTMLElement>('.resources-menu__news-container'),
      megaMenuRightPanel: this.el.querySelector<HTMLElement>('.mega-menu__about-container')
    };

    if (this.dom.searchMenuBtn) {
      (this.dom.searchMenuBtn as HTMLButtonElement).disabled = true;
    }
  }

  addListeners() {
    if (this.dom.resourcesTriggerBtn) {
      (this.dom.resourcesTriggerBtn as HTMLElement).addEventListener(
        'click',
        this.toggleResources.bind(this)
      );
    }

    if (this.dom.mobileResourcesTriggerBtn) {
      (this.dom.mobileResourcesTriggerBtn as HTMLElement).addEventListener(
        'click',
        this.toggleResources.bind(this)
      );
    }

    if (this.dom.resourcesCloseBtn) {
      (this.dom.resourcesCloseBtn as HTMLElement).addEventListener(
        'click',
        this.toggleResources.bind(this)
      );
    }

    if (this.dom.megaMenuTriggerBtn) {
      (this.dom.megaMenuTriggerBtn as HTMLElement).addEventListener(
        'click',
        this.toggleMegaMenu.bind(this)
      );
    }

    if (this.dom.megaMenuCloseBtn) {
      (this.dom.megaMenuCloseBtn as HTMLElement).addEventListener(
        'click',
        this.toggleMegaMenu.bind(this)
      );
    }
    (this.dom.megamenuBackTo as NodeList).forEach(item => {
      item.addEventListener('click', this.backToSection.bind(this));
    });

    (this.dom.megamenuItems as NodeList).forEach(item => {
      item.addEventListener('click', this.openItemSection.bind(this));
    });

    if (this.dom.searchTriggerBtn) {
      (this.dom.searchTriggerBtn as HTMLElement).addEventListener(
        'click',
        this.toggleSearch.bind(this)
      );
    }

    if (this.dom.searchMenuCloseBtn) {
      (this.dom.searchMenuCloseBtn as HTMLElement).addEventListener(
        'click',
        this.toggleSearch.bind(this)
      );
    }

    if (this.dom.searchMenuBtn) {
      (this.dom.searchMenuBtn as HTMLElement).addEventListener('click', () => {
        (this.dom.searchMenuForm as HTMLFormElement).submit();
      });
    }

    if (this.dom.searchMenuForm) {
      (this.dom.searchMenuForm as HTMLFormElement).addEventListener('submit', e => {
        e.preventDefault();
        if ((this.dom.searchMenuBtn as HTMLButtonElement).disabled === true) return;
        const searchInput = (this.dom.searchInput as HTMLInputElement).value;
        const formAction = (this.dom.searchMenuForm as HTMLFormElement).action;
        window.location.href = `${formAction}?search-term="${searchInput}"&_from="_Search"`;
      });
    }

    const handleSearch = (e: Event) => {
      const searchBtn = this.dom.megaMenuSearchBtn as HTMLButtonElement;
      const searchInput = this.dom.megaMenuSearchInput as HTMLInputElement;
      const searchForm = this.dom.megaMenuSearchForm as HTMLFormElement;

      e.preventDefault();
      if (searchBtn.disabled) return;
      const searchValue = searchInput.value;
      const formAction = searchForm.action;
      window.location.href = `${formAction}?search-term="${searchValue}"&_from="_Search"`;
    };

    if (this.dom.megaMenuSearchForm) {
      const searchForm = this.dom.megaMenuSearchForm as HTMLFormElement;

      searchForm.addEventListener('submit', handleSearch);
    }

    if (this.dom.searchInput) {
      const updateButtonState = () => {
        const inputValue = (this.dom.searchInput as HTMLInputElement).value;
        (this.dom.searchMenuBtn as HTMLButtonElement).disabled = inputValue.length === 0;
      };

      (this.dom.searchInput as HTMLInputElement).addEventListener('input', updateButtonState);

      (this.dom.searchInput as HTMLInputElement).addEventListener('keydown', e => {
        if (e.key === 'Enter') {
          e.preventDefault();
          if (!(this.dom.searchMenuBtn as HTMLButtonElement).disabled) {
            (this.dom.searchMenuForm as HTMLFormElement).submit();
          }
        }
      });
    }

    if (this.dom.megaMenuSearchInput) {
      const megaMenuUpdateButtonState = () => {
        const inputValue = (this.dom.megaMenuSearchInput as HTMLInputElement).value;
        (this.dom.megaMenuSearchBtn as HTMLButtonElement).disabled = inputValue.length === 0;
      };
      (this.dom.megaMenuSearchInput as HTMLInputElement).addEventListener(
        'input',
        megaMenuUpdateButtonState
      );
      (this.dom.megaMenuSearchInput as HTMLInputElement).addEventListener('keydown', e => {
        if (e.key === 'Enter') {
          e.preventDefault();
          if (!(this.dom.megaMenuSearchBtn as HTMLButtonElement).disabled) {
            handleSearch(e);
          }
        }
      });
      megaMenuUpdateButtonState();
    }

    if (isTabletLandscape() || isDesktop()) {
      (this.dom.resourcesMenuItems as NodeList).forEach(item => {
        item.addEventListener('click', this.openItemSection.bind(this));
      });
      document.addEventListener('keydown', this.handleKeyDown.bind(this));
    }
  }

  backToSection(e: Event) {
    const target = e.currentTarget as HTMLElement;
    target.parentElement?.classList.remove('is-active');
  }

  openItemSection(e: Event) {
    const target = e.currentTarget as HTMLElement;
    const targetPanel = target.getAttribute('aria-controls');

    (this.dom.resourcesMenuPanels as NodeList).forEach(panel => {
      if ((panel as HTMLElement).id === targetPanel) {
        gsap.set(panel, { x: window.innerWidth / 2 });
        gsap
          .timeline({
            onStart: () => {
              (panel as HTMLElement).classList.add('is-active');
            }
          })
          .fromTo(
            panel,
            {
              x: window.innerWidth / 2,
              opacity: 0
            },
            {
              x: 0,
              opacity: 1,
              duration: prefersReducedMotion() ? 0 : 0.8,
              ease: 'power1.inOut'
            }
          );
        gsap
          .timeline()
          .fromTo(
            this.dom.resourcesRightPanel,
            {
              opacity: 1,
              x: 0
            },
            {
              x: window.innerWidth / 2,
              opacity: 0,
              duration: prefersReducedMotion() ? 0 : 0.8,
              ease: 'power1.inOut'
            }
          )
          .set(this.dom.resourcesRightPanel, { display: 'none' });
        if (this.dom.resourcesMenuDecorator) {
          (this.dom.resourcesMenuDecorator as HTMLElement).style.opacity = '1';
          (this.dom.resourcesMenuDecorator as HTMLElement).style.setProperty(
            '--inset-block-start',
            `${target.offsetTop}px`
          );
        }
      } else {
        gsap
          .timeline({
            onComplete: () => {
              (panel as HTMLElement).classList.remove('is-active');
            }
          })
          .fromTo(
            panel,
            {
              x: 0,
              opacity: 1
            },
            {
              x: window.innerWidth / 2,
              opacity: 0,
              duration: prefersReducedMotion() ? 0 : 0.8,
              ease: 'power1.inOut'
            }
          );
      }
    });

    (this.dom.megamenuPanels as NodeList).forEach(panel => {
      if ((panel as HTMLElement).id === targetPanel) {
        // if panel has a class of 'is-active' then we dont need to animate it
        if ((panel as HTMLElement).classList.contains('is-active')) {
          return;
        }

        gsap.set(panel, { x: window.innerWidth / 2 });
        gsap
          .timeline({
            onStart: () => {
              (panel as HTMLElement).classList.add('is-active');
            }
          })
          .fromTo(
            panel,
            {
              x: window.innerWidth / 2,
              opacity: 0
            },
            {
              x: 0,
              opacity: 1,
              duration: prefersReducedMotion() ? 0 : 0.7,
              ease: 'power1.inOut'
            }
          );
        gsap
          .timeline()
          .fromTo(
            this.dom.megaMenuRightPanel,
            {
              opacity: 1,
              x: 0
            },
            {
              x: window.innerWidth / 2,
              opacity: 0,
              duration: prefersReducedMotion() ? 0 : 0.7,
              ease: 'power1.inOut'
            }
          )
          .set(this.dom.megaMenuRightPanel, { display: 'none' });
        if (this.dom.megamenuDecorator) {
          (this.dom.megamenuDecorator as HTMLElement).style.opacity = '1';
          (this.dom.megamenuDecorator as HTMLElement).style.setProperty(
            '--inset-block-start',
            `${target.offsetTop}px`
          );
        }
      } else {
        gsap
          .timeline({
            onComplete: () => {
              (panel as HTMLElement).classList.remove('is-active');
            }
          })
          .fromTo(
            panel,
            {
              x: 0,
              opacity: 1
            },
            {
              x: window.innerWidth / 2,
              opacity: 0,
              duration: prefersReducedMotion() ? 0 : 0.7,
              ease: 'power1.inOut'
            }
          );
      }
    });
  }

  toggleResources() {
    gsap.set(this.dom.resourcesMenu, { y: window.innerHeight * -1 });

    const isOpen = (this.dom.resourcesMenu as HTMLElement).classList.toggle('open');
    if (isTabletLandscape()) {
      document.body.classList.toggle('no-scroll', isOpen);
    }
    if (isOpen) {
      (this.dom.resourcesMenuPanels as NodeList).forEach(panel => {
        (panel as HTMLElement).classList.remove('is-active');
      });
      (this.dom.resourcesMenu as HTMLElement).style.display = 'block';
    }
    requestAnimationFrame(() => {
      this.setupClickResourcesTimeline(isOpen);
    });
    (this.dom.resourcesTriggerBtn as HTMLElement).setAttribute('aria-expanded', String(isOpen));
    this.manageFocus(this.dom.resourcesMenu as HTMLElement, isOpen);
  }

  manageFocus(menu: HTMLElement, isOpen: boolean) {
    if (isOpen) {
      const firstFocusableElement = menu.querySelector<HTMLElement>(
        'button, a, [tabindex]:not([tabindex="-1"])'
      );
      firstFocusableElement?.focus();
    } else {
      const triggerButton = (this.dom.el as HTMLElement).querySelector<HTMLElement>(
        `[aria-controls="${menu.id}"]`
      );
      triggerButton?.focus();
    }
  }

  handleKeyDown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      if ((this.dom.megaMenu as HTMLElement).classList.contains('open')) {
        this.toggleMegaMenu();
      }
      if ((this.dom.resourcesMenu as HTMLElement).classList.contains('open')) {
        this.toggleResources();
      }
      if ((this.dom.searchMenu as HTMLElement).classList.contains('open')) {
        this.toggleSearch();
      }
    }
    e.stopPropagation();
  }

  toggleMegaMenu() {
    gsap.set(this.dom.megaMenu, { y: window.innerHeight * -1 });
    const isOpen = (this.dom.megaMenu as HTMLElement).classList.toggle('open');

    document.body.classList.toggle('no-scroll', isOpen);
    if (isOpen) {
      (this.dom.megamenuPanels as NodeList).forEach(panel => {
        (panel as HTMLElement).classList.remove('is-active');
      });
      (this.dom.megaMenu as HTMLElement).style.display = 'block';
    }
    requestAnimationFrame(() => {
      this.setupClickMegaMenuTimeline(isOpen);
    });
    (this.dom.megaMenuTriggerBtn as HTMLElement).setAttribute('aria-expanded', String(isOpen));
    this.manageFocus(this.dom.megaMenu as HTMLElement, isOpen);
  }

  toggleSearch() {
    const isOpen = (this.dom.searchMenu as HTMLElement).classList.toggle('open');
    (this.dom.searchTriggerBtn as HTMLElement).setAttribute('aria-expanded', String(isOpen));
    this.dom.searchMenuForm = this.el.querySelector<HTMLElement>(
      '.search-menu__search-container form'
    );

    this.manageFocus(this.dom.searchMenu as HTMLElement, isOpen);
  }

  setupClickResourcesTimeline(isOpen: boolean) {
    const resourcesTimeline = gsap.timeline();

    if (isOpen) {
      resourcesTimeline
        .fromTo(
          this.dom.resourcesMenu,
          { y: isTabletLandscape() ? window.innerHeight * -1 : window.innerHeight },
          { y: 0, duration: prefersReducedMotion() ? 0 : 0.7, ease: 'power1.inOut' }
        )
        .fromTo(
          this.dom.resourcesMenuContent,
          { opacity: 0 },
          { opacity: 1, duration: prefersReducedMotion() ? 0 : 0.4, ease: 'power1.inOut' },
          '-=0.3'
        );
    } else {
      resourcesTimeline
        .fromTo(
          this.dom.resourcesMenu,
          { y: 0 },
          {
            y: isTabletLandscape() ? window.innerHeight * -1 : window.innerHeight,
            duration: prefersReducedMotion() ? 0 : 0.7,
            ease: 'power1.inOut',
            onComplete: () => {
              (this.dom.resourcesMenu as HTMLElement).style.display = 'none';
            }
          }
        )
        .fromTo(
          this.dom.resourcesMenuContent,
          { opacity: 1 },
          { opacity: 0, duration: prefersReducedMotion() ? 0 : 0.4, ease: 'power1.inOut' },
          '-=0.4'
        );
    }
  }

  setupClickMegaMenuTimeline(isOpen: boolean) {
    const megaMenuTimeline = gsap.timeline({
      onComplete: () => {
        if (!isOpen) {
          (this.dom.megamenuDecorator as HTMLElement)?.removeAttribute('style');
          (this.dom.megaMenuRightPanel as HTMLElement)?.removeAttribute('style');
        }
      }
    });

    if (isOpen) {
      megaMenuTimeline
        .fromTo(
          this.dom.megaMenu,
          { y: window.innerHeight * -1 },
          { y: 0, duration: prefersReducedMotion() ? 0 : 0.7, ease: 'power1.inOut' }
        )
        .fromTo(
          this.dom.megamenuContent,
          { opacity: 0 },
          { opacity: 1, duration: prefersReducedMotion() ? 0 : 0.4, ease: 'power1.inOut' },
          '-=0.3'
        );
    } else {
      megaMenuTimeline
        .fromTo(
          this.dom.megamenuContent,
          { opacity: 1 },
          { opacity: 0, duration: prefersReducedMotion() ? 0 : 0.4, ease: 'power1.inOut' }
        )
        .fromTo(
          this.dom.megaMenu,
          { y: 0 },
          {
            y: window.innerHeight * -1,
            duration: prefersReducedMotion() ? 0 : 0.7,
            ease: 'power1.inOut',
            onComplete: () => {
              (this.dom.megaMenu as HTMLElement).style.display = 'none';
            }
          },
          '-=0.4'
        );
    }
  }
}

export default Header;
